import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from '@react-router';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from '@shared/core';
import { useEventNameMatch } from '@shared/components/AuthProvider/AuthProvider.utils';
import { withWindow } from '@shared/utils/withWindow';
import { useTimeout } from '@shared/hooks/useTimeout';
import { VerifyUnlockSessionDocument } from './FindInviteSuccessForm.graphql.generated';
import type { VerifyUnlockSessionQuery, VerifyUnlockSessionQueryVariables } from './FindInviteSuccessForm.graphql.generated';
import type { SuccessFormProps } from './FindInviteSuccessForm';

const MAX_SESSION_VERIFY_ATTEMPTS = 10;
const SESSION_VERIFY_INTERVAL_MS = 500;

export const useFindInviteSuccessController = ({ guest, findInviteSuccessBehavior }: SuccessFormProps) => {
  const location = useLocation();
  const { eventName } = useEventNameMatch();
  const client = useApolloClient();
  const { t, t2 } = useTranslation('guestUnlock');
  const title = t('findInviteSuccessForm', 'title')({ firstName: guest.firstName });
  const { subtitle, submitButtonText } = t2('findInviteSuccessForm');
  const [verifying, setVerifying] = useState(false);
  const { set: setPollTimeout } = useTimeout();
  const cancelledRef = useRef(false);

  // If the dialog unmounts mid-poll, stop scheduling further polls and skip
  // the redirect — otherwise a slow session verification would force a
  // full-page nav from whatever screen the user navigated to.
  useEffect(
    () => () => {
      cancelledRef.current = true;
    },
    [],
  );

  const onSubmitHandler = useCallback(() => {
    if (!eventName) {
      return;
    }

    setVerifying(true);

    // Single full-page navigation. We avoid onDialogClose() + a separate nav —
    // two competing navigations would race. Schedule lands the guest on
    // /schedule; the generic unlock entries reload the page they're already on.
    const navigate = () => {
      withWindow(global => {
        if (findInviteSuccessBehavior === 'navigateToSchedule') {
          global.location.assign(`/${eventName}/schedule${location.search}`);
        } else {
          global.location.reload();
        }
      });
    };

    // Poll for the joy_session_id cookie to propagate. In-app browsers
    // (WhatsApp, LINE, etc.) flush Set-Cookie from XHR responses asynchronously,
    // so the cookie set by joinEvent may not be available immediately for the
    // next request. We re-check until activeSession.person is non-null. If we
    // time out we navigate anyway — the user explicitly clicked and the session
    // may still work on the full page load.
    const pollSession = async (attempt: number): Promise<void> => {
      if (cancelledRef.current) return;

      try {
        const { data } = await client.query<VerifyUnlockSessionQuery, VerifyUnlockSessionQueryVariables>({
          query: VerifyUnlockSessionDocument,
          variables: { name: eventName },
          fetchPolicy: 'network-only',
        });

        if (cancelledRef.current) return;

        if (data?.eventByName?.activeSession?.person?.id) {
          navigate();
          return;
        }
      } catch {
        // Network hiccup — keep retrying.
        if (cancelledRef.current) return;
      }

      if (attempt + 1 >= MAX_SESSION_VERIFY_ATTEMPTS) {
        navigate();
        return;
      }

      setPollTimeout(() => pollSession(attempt + 1), SESSION_VERIFY_INTERVAL_MS);
    };

    pollSession(0);
  }, [client, eventName, location.search, setPollTimeout, findInviteSuccessBehavior]);

  return {
    title,
    subtitle,
    submitButtonText,
    onSubmitHandler,
    verifying,
  };
};
